import useSourcelabForm, { isLayout } from '@alphabait/ui/form'
import { ArrowLeft, Trash } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { renderCustomField } from '../../render-custom-field'
import { getCustomCheckboxProperties } from '../../custom-checkbox'
import { getCustomDateProperties } from '../../custom-date'
import { getCustomEmailProperties } from '../../custom-email'
import { getCustomInputProperties } from '../../custom-input'
import { getCustomRadioGenderProperties } from '../../custom-radio-gender'
import { getCustomShadcnSelectProperties } from '../../custom-shadcn-select'
import { getCustomSwitchProperties } from '../../custom-switch'
import { getCustomTextareaProperties } from '../../custom-textarea'
import { AlphaTypes } from '../../custom-types'
import { getCustomRadioGroupProperties } from '../../ec-flow-components/custom-radio-group'
import { getCustomPictureChoiceProperties } from '../../ec-flow-components/picture-choice'

function hasId(obj: unknown): obj is { id: string } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'id' in obj &&
    typeof (obj as { id: unknown }).id === 'string'
  )
}

// Type guard for fields with a 'name' property
function hasName(field: unknown): field is { name: string } {
  return (
    typeof field === 'object' &&
    field !== null &&
    'name' in field &&
    typeof (field as { name: unknown }).name === 'string'
  )
}

// Type guard for uiOptions with label
function hasLabel(uiOptions: unknown): uiOptions is { label: string } {
  return (
    typeof uiOptions === 'object' &&
    uiOptions !== null &&
    'label' in uiOptions &&
    typeof (uiOptions as { label: unknown }).label === 'string'
  )
}

// Helper function to set a value at a nested path
function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  if (!path || path === '') return { ...obj }

  const parts = path.split('.')
  const newObj = { ...obj }
  let current: Record<string, unknown> = newObj

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]
    const nextValue = current[part]
    current[part] =
      nextValue !== undefined &&
        typeof nextValue === 'object' &&
        nextValue !== null
        ? { ...(nextValue as Record<string, unknown>) }
        : {}
    current = current[part] as Record<string, unknown>
  }

  current[parts[parts.length - 1]] = value
  return newObj
}

// Get configuration options based on field type
const getOptions = (field: AlphaTypes): AlphaTypes[] => {
  if (isLayout(field)) return []
  if (field.type === 'custom' && field.customControlType === 'textarea')
    return getCustomTextareaProperties()
  if (field.type === 'custom' && field.customControlType === 'shadcn-select') {
    return getCustomShadcnSelectProperties()
  }
  if (field.type === 'custom' && field.customControlType === 'picture-choice') {
    return getCustomPictureChoiceProperties()
  }
  if (field.type === 'custom' && field.customControlType === 'dateInput') {
    return getCustomDateProperties()
  }
  if (field.type === 'input' && field.uiOptions?.inputType === 'checkbox')
    return getCustomCheckboxProperties()
  if (field.type === 'custom' && field.customControlType === 'switch')
    return getCustomSwitchProperties()
  if (field.type === 'input') {
    return getCustomInputProperties()
  }
  if (field.type === 'custom' && field.customControlType === 'radioGroupGender')
    return getCustomRadioGenderProperties()
  if (field.type === 'custom' && field.customControlType === 'radioGroup')
    return getCustomRadioGroupProperties()
  if (field.type === 'custom' && field.customControlType === 'email')
    return getCustomEmailProperties()

  return []
}

export default function QuestionnaireEditor({
  selectedField,
  setSelectedField,
  setFields,
  formFields,
}: {
  selectedField: AlphaTypes | null
  setSelectedField: (f: AlphaTypes | null) => void
  setFields: (f: AlphaTypes[]) => void
  formFields: AlphaTypes[]
}) {
  const { t } = useTranslation()

  // Use useMemo to regenerate the fields when the selected field changes
  const fieldOptions = useMemo(() => {
    return selectedField ? getOptions(selectedField) : []
  }, [selectedField])

  // Handler for field changes
  const handleFieldChange = (key: string, newValue: { old: any; new: any }) => {
    if (!selectedField) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updatedField: any = setNestedValue(
      selectedField as Record<string, unknown>,
      key,
      newValue.new,
    )
    // If we're changing the label, update the field name too
    if (key === 'uiOptions.label' && typeof newValue.new === 'string') {
      // Allow editing during the editing process
      if (updatedField.uiOptions) {
        updatedField.uiOptions.label = newValue.new
      }

      // For the name field, we need to ensure it's never completely empty
      if (newValue.new.trim() !== '') {
        updatedField.name = newValue.new
      } else {
        updatedField.name =
          ('name' in selectedField ? selectedField.name : undefined) ||
          'field_' + Date.now()
      }

    }

    // Update the field with the new data
    const updatedFields = formFields.map((f: AlphaTypes) => {
      if (
        !isLayout(f) &&
        hasId(f) &&
        hasId(selectedField) &&
        f.id === selectedField.id
      ) {
        return updatedField as AlphaTypes
      }
      return f
    })
    // Update the fields in the form
    setFields(updatedFields)
    // Update the selected field to show the changes
    setSelectedField(updatedField as AlphaTypes)
  }

  // Early return if no field is selected
  if (!selectedField) {
    return null
  }

  // Use the form hook from ui-components with custom rendering
  // The entire component is remounted when selectedField.id changes (via key prop)
  // so the form state is automatically reset
  const { render } = useSourcelabForm({
    values: selectedField as Record<string, any>,
    customRender: renderCustomField,
    mode: 'onBlur',
    onFieldChange: handleFieldChange,
  })

  // Function to delete a field from the form
  const handleDeleteField = (fieldId: string) => {
    const updatedFields = formFields.filter(
      (field: AlphaTypes) => 'id' in field && field.id !== fieldId,
    )
    setFields(updatedFields)
    if (
      selectedField &&
      'id' in selectedField &&
      selectedField.id === fieldId
    ) {
      setSelectedField(null)
    }
  }
  return (
    // Configuration panel for selected field
    <div className="field-configuration">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <button
            onClick={() => setSelectedField(null)}
            className="text-gray-600 hover:text-blue-600 mr-2 items-center"
            aria-label="Back to Components"
          >
            <ArrowLeft size={26} />
          </button>
          <h3 className="text-lg font-semibold">
            {t('questionnaire_builder.blocks')}
          </h3>
        </div>
      </div>
      <div className="config-panel p-4 rounded-lg">
        <div className="flex flex-col h-full">
          <div className="mb-4">
            <span className="block font-bold text-lg mb-2">
              {hasLabel(selectedField.uiOptions)
                ? selectedField.uiOptions.label
                : hasName(selectedField)
                  ? selectedField.name
                  : ''}
            </span>
          </div>

          <div>
            {render(fieldOptions)}
          </div>
          {/* Delete Question button at bottom */}
          <div className="mt-6 text-center">
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (
                  selectedField &&
                  !isLayout(selectedField) &&
                  hasId(selectedField)
                ) {
                  handleDeleteField(selectedField.id)
                }
              }}
              className="flex items-center bg-white justify-center w-full hover:text-red-700 py-2 border-2 rounded-md"
            >
              <Trash size={16} className="mr-2" />
              {t('questionnaire_builder.delete_question')}
            </button>

            {/* Back to available fields button */}
            <button
              onClick={() => setSelectedField(null)}
              className="flex items-center !text-white justify-center w-full py-2 border-2 rounded-md mt-2 bg-primary hover:bg-marine-300 focus:bg-primary focus-visible:outline focus:ring-primary"
            >
              {t('questionnaire_builder.add_new_question')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
